<template>
    <div>
        <div v-if="loading">
            <loading></loading>
        </div>

        <div v-else>
            <b-alert variant="danger" :show="hasErrors">
                <span v-html="errorMessage"></span>
            </b-alert>

            <!-- filter -->
            <b-row v-if="!hasErrors" class="mb-3">
                <b-col md="3" class="my-1">
                    <validation-filter />
                </b-col>
                <b-col md="3" class="my-1"></b-col>

                <b-col md="4" offset-md="2" class="my-1">
                    <b-form-group class="mb-0">
                        <b-input-group size="sm">
                            <b-form-input
                                v-model="filter"
                                placeholder="Entrer la valeur recherchée"
                            />
                            <b-input-group-append>
                                <b-button
                                    :disabled="!filter"
                                    @click="resetSearch"
                                >
                                    Effacer
                                </b-button>
                            </b-input-group-append>
                        </b-input-group>
                    </b-form-group>
                </b-col>
            </b-row>
            <!-- /filter -->

            <!-- Table -->
            <b-table
                responsive
                small
                :items="items"
                :fields="fields"
                :filter="filter"
                :filter-included-fields="['search']"
                :per-page="perPage"
                :current-page="currentPage"
                @filtered="onFiltered"
            >
                <template #cell(id)="data">
                    <delete-client-handler
                        :id="data.item.id"
                        :can-be-deleted="data.item.can_be_deleted"
                    />
                </template>
                <template #cell(name)="data">
                    <b-link @click="goToHome(data.item.url)"
                    >{{ data.item.name }}
                    </b-link>
                </template>
                <template #cell(balance_priority)="data">
                    <table-priority-handler
                        :key="'tph' + data.item.id"
                        :client="data.item"
                    />
                </template>
                <template #cell(needs_supervision)="data">
                    <table-supervision-flag-handler
                        :key="'tsfh' + data.item.id"
                        :client="data.item"
                        @supervision-flag-has-changed="getItems"
                    />
                </template>
                <template #cell(needs_appointment)="data">
                    <table-appointment-flag-handler
                        :key="'tafh' + data.item.id"
                        :client="data.item"
                        @appointment-flag-has-changed="getItems"
                    />
                </template>
                <template #cell(EXP.initials)="data">
                    <table-role-updater
                        v-if="data.item.hasOwnProperty('EXP')"
                        :key="'truEXP' + data.item.id"
                        :role-assignment="data.item.EXP"
                    />
                </template>
                <template #cell(MAN.initials)="data">
                    <table-role-updater
                        v-if="data.item.hasOwnProperty('MAN')"
                        :key="'truMAN' + data.item.id"
                        :role-assignment="data.item.MAN"
                    />
                </template>
                <template #cell(SUP.initials)="data">
                    <table-role-updater
                        v-if="data.item.hasOwnProperty('SUP')"
                        :key="'truSUP' + data.item.id"
                        :role-assignment="data.item.SUP"
                    />
                </template>
                <template #cell(REV.initials)="data">
                    <table-role-updater
                        v-if="data.item.hasOwnProperty('REV')"
                        :key="'truREV' + data.item.id"
                        :role-assignment="data.item.REV"
                    />
                </template>
                <template #cell(CPT.initials)="data">
                    <table-role-updater
                        v-if="data.item.hasOwnProperty('CPT')"
                        :key="'truCPT' + data.item.id"
                        :role-assignment="data.item.CPT"
                    />
                </template>
                <template #cell(JUR.initials)="data">
                    <table-role-updater
                        v-if="data.item.hasOwnProperty('JUR')"
                        :key="'truJUR' + data.item.id"
                        :role-assignment="data.item.JUR"
                    />
                </template>
                <template #cell(SOC.initials)="data">
                    <table-role-updater
                        v-if="data.item.hasOwnProperty('SOC')"
                        :key="'truSOC' + data.item.id"
                        :role-assignment="data.item.SOC"
                    />
                    <table-role-creator
                        v-else
                        :key="'trcSOC' + data.item.id"
                        :client="data.item"
                        :type="'SOC'"
                    />
                </template>
                <template #cell(RDV.initials)="data">
                    <table-role-updater
                        v-if="data.item.hasOwnProperty('RDV')"
                        :key="'truRDV' + data.item.id"
                        :role-assignment="data.item.RDV"
                    />
                    <table-role-creator
                        v-else
                        :key="'trcRDV' + data.item.id"
                        :client="data.item"
                        :role-option="'RDV'"
                    />
                </template>
                <template #cell(is_valid)="data">
                    <validate-client-handler
                        v-if="!data.item.is_valid"
                        :id="data.item.id"
                        :can-be-validated="data.item.can_be_validated"
                    />
                </template>
            </b-table>
            <!-- /Table -->

            <!-- Pagination -->
            <b-row>
                <b-col md="3" class="my-1 text-center">
                    <b-pagination
                        v-if="totalRows > 0"
                        v-model="currentPage"
                        :total-rows="totalRows"
                        :per-page="perPage"
                        class="my-0"
                        size="sm"
                    ></b-pagination>
                </b-col>
                <b-col md="6" class="my-2">
                    <p class="text-center small pt-0">{{ legend }}</p>
                </b-col>
                <b-col md="3" class="my-1 text-right">
                    <b-dropdown
                        right
                        split
                        size="sm"
                        :text="perPage + ' éléments par page'"
                    >
                        <b-dropdown-item
                            v-for="pageOption in pageOptions"
                            :key="pageOption"
                            @click="setPerPage(pageOption)"
                        >
                            {{ pageOption }}
                        </b-dropdown-item>
                    </b-dropdown>
                </b-col>
            </b-row>
            <!-- /Pagination -->
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'

import loading from '../../../common/components/loading'
import DeleteClientHandler from '../components/DeleteClientHandler'
import ValidateClientHandler from '../components/ValidateClientHandler'
import ValidationFilter from '../components/ValidationFilter'
import ActivationFilter from '../components/ActivationFilter'

import TableHandler from '../../../common/mixins/TableHandler'
import TablePriorityHandler from '../components/TablePriorityHandler'
import TableRoleUpdater from '../components/TableRoleUpdater'
import TableRoleCreator from '../components/TableRoleCreator'
import TableSupervisionFlagHandler from '../components/TableSupervisionFlagHandler'
import TableAppointmentFlagHandler from '../components/TableAppointmentFlagHandler'

export default {
    name: 'TableClients',

    components: {
        TableSupervisionFlagHandler,
        TableAppointmentFlagHandler,
        TableRoleCreator,
        TableRoleUpdater,
        TablePriorityHandler,
        DeleteClientHandler,
        ValidateClientHandler,
        loading,
        ValidationFilter,
        ActivationFilter
    },

    mixins: [TableHandler],

    data() {
        return {
            fields: [
                {
                    key: 'id',
                    label: '',
                    class: 'td-sm td-icon align-middle',
                    sortable: false
                },
                {
                    key: 'unique_code',
                    label: 'Code',
                    class: 'td-sm td-bold align-middle',
                    sortable: true
                },
                {
                    key: 'name',
                    label: 'Raison sociale',
                    class: 'td-sm td-bold align-middle',
                    sortable: true
                },
                {
                    key: 'accounting_firm_company.code',
                    label: 'Cab. EXP',
                    class: 'td-sm text-center align-middle',
                    sortable: true
                },
                {
                    key: 'accounting_firm_company2.code',
                    label: 'Cab. SOC',
                    class: 'td-sm text-center align-middle',
                    sortable: true
                },
                {
                    key: 'balance_priority',
                    label: 'Priorité',
                    class: 'td-sm text-center align-middle',
                    sortable: true
                },
                {
                    key: 'needs_supervision',
                    label: 'Superv.',
                    class: 'td-sm td-icon align-middle',
                    sortable: true
                },
                {
                    key: 'needs_appointment',
                    label: 'RDV',
                    class: 'td-sm td-icon align-middle',
                    sortable: true
                },
                {
                    key: 'legal_form',
                    label: 'Type',
                    class: 'td-sm text-center align-middle',
                    sortable: true
                },
                {
                    key: 'vat',
                    label: 'Régime TVA',
                    class: 'td-sm align-middle',
                    sortable: true
                },
                {
                    key: 'EXP.initials',
                    label: 'EXP',
                    class: 'td-sm text-center align-middle',
                    sortable: true
                },
                {
                    key: 'MAN.initials',
                    label: 'MAN',
                    class: 'td-sm text-center align-middle',
                    sortable: true
                },
                {
                    key: 'SUP.initials',
                    label: 'SUP',
                    class: 'td-sm text-center align-middle',
                    sortable: true
                },
                {
                    key: 'REV.initials',
                    label: 'REV',
                    class: 'td-sm text-center align-middle',
                    sortable: true
                },
                {
                    key: 'CPT.initials',
                    label: 'CPT',
                    class: 'td-sm text-center align-middle',
                    sortable: true
                },
                {
                    key: 'JUR.initials',
                    label: 'JUR',
                    class: 'td-sm text-center align-middle',
                    sortable: true
                },
                {
                    key: 'SOC.initials',
                    label: 'SOC',
                    class: 'td-sm text-center align-middle',
                    sortable: true
                },
                {
                    key: 'RDV.initials',
                    label: 'RDV',
                    class: 'td-sm text-center align-middle',
                    sortable: true
                },
                {
                    key: 'is_valid',
                    label: '',
                    class: 'td-sm align-middle',
                    sortable: false
                }
            ],
            unfilteredItems: []
        }
    },

    async created() {
        this.filterable = false
        this.$store.commit('browsing/SET_PAGE_NAME', 'clients')
        this.$store.commit('browsing/INIT_CLIENT_ACTIVATION_FILTER')
        this.$store.commit('browsing/INIT_CLIENT_VALIDATION_FILTER')
        await this.getItems()
        await this.$store.dispatch('data/getAllOptions')
    },

    methods: {
        goToHome(url) {
            this.$store.commit('reportingVM/RESET_SEARCH')
            window.location.href = url
        },

        async getItems() {
            try {
                this.loading = true
                const response = await axios.get('/clients')
                this.unfilteredItems = response.data.data
                this.filterItems()
                this.loading = false
            } catch (error) {
                console.log(error)
                this.handleErrors(error.response)
            }
        },

        filterItems() {
            this.items = this.unfilteredItems
            switch (this.validationFilter) {
                case 'valid':
                    this.items = this.items.filter((item) => item.is_valid)
                    break
                case 'invalid':
                    this.items = this.items.filter((item) => !item.is_valid)
                    break
                default:
                    break
            }
            switch (this.activationFilter) {
                case 'active':
                    this.items = this.items.filter((item) => item.is_active)
                    break
                case 'old':
                    this.items = this.items.filter((item) => !item.is_active)
                    break
                default:
                    break
            }
            this.updateTotalRows()
        }
    },

    computed: {
        ...mapState({
            menuRoutes: (state) => state.clientVM.menuRoutes,
            viewAllClients: (state) => state.reportingVM.viewAllClients,
            validationFilter: (state) => state.browsing.clientsValidationFilter,
            activationFilter: (state) => state.browsing.clientsActivationFilter
        })
    }
}
</script>

<style scoped></style>
